import React from 'react';
import { Star, TrendingDown, Truck, Boxes } from 'lucide-react';

/**
 * 4-card decision-support insight header.
 * Computes its own metrics from the `suppliers` list — caller just passes the data.
 */
const SummaryHeader = ({ suppliers, t }) => {
  if (!suppliers || suppliers.length === 0) return null;

  const bestSupplier = suppliers[0];
  const cheapestSupplier = [...suppliers].sort((a, b) => a.price - b.price)[0];
  const fastestSupplier = [...suppliers].sort((a, b) => a.lead_time_days - b.lead_time_days)[0];
  const lowestMoqSupplier = [...suppliers].sort((a, b) => a.moq - b.moq)[0];

  const cheapestPriceLabel = cheapestSupplier?.price_range_low
    ? `$${cheapestSupplier.price_range_low} – $${cheapestSupplier.price_range_high}`
    : `$${cheapestSupplier?.price}`;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="results-summary">
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="summary-top-supplier">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
            <Star className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <span className="text-xs text-slate-500">{t('topSupplier')}</span>
            <p className="text-sm font-bold text-slate-900 truncate">{bestSupplier?.name}</p>
            <p className="text-xs text-slate-500">{bestSupplier?.score_breakdown?.total ?? bestSupplier?.reliability_score}/100</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="summary-best-price">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center shrink-0">
            <TrendingDown className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0">
            <span className="text-xs text-slate-500">{t('bestPriceInsight')}</span>
            <p className="text-sm font-bold text-slate-900">{cheapestPriceLabel}<span className="text-xs font-normal text-slate-500">{t('perUnit')}</span></p>
            <p className="text-xs text-slate-500 truncate">{cheapestSupplier?.name}</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="summary-fastest">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <span className="text-xs text-slate-500">{t('fastestSupplier')}</span>
            <p className="text-sm font-bold text-slate-900">{fastestSupplier?.lead_time_days} {t('days')}</p>
            <p className="text-xs text-slate-500 truncate">{fastestSupplier?.name}</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="summary-lowest-moq">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center shrink-0">
            <Boxes className="w-5 h-5 text-violet-600" />
          </div>
          <div className="min-w-0">
            <span className="text-xs text-slate-500">{t('lowestMoqInsight')}</span>
            <p className="text-sm font-bold text-slate-900">{lowestMoqSupplier?.moq} {t('units')}</p>
            <p className="text-xs text-slate-500 truncate">{lowestMoqSupplier?.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryHeader;
